import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import T from '../styles/theme';
import Icon from '../components/Icon';
import { useStore } from '../data/store';
import { useAuth } from '../data/auth';
import { useToast } from '../components/Toast';
import supabase from '../data/supabase';
import apiFetch from '../data/api';

/* ═══════════════════════════════════════════════════════
   TEAM CHAT — Full Slack-replacement inside CoreFlow
   Channels, DMs, real-time, @mentions, file sharing,
   workflow messages, AI analytics
   ═══════════════════════════════════════════════════════ */

const C = {
  bg: "#F5F6FA", bg2: "rgba(255,255,255,0.60)", bg3: "rgba(255,255,255,0.80)",
  sidebarBg: "rgba(255,255,255,0.45)", sidebarHover: "rgba(64,89,173,0.05)",
  txt: "#2C2E33", tx2: "#6B7280", tx3: "#9CA3AF",
  blue: "#FAFAFA", grn: "#2E8B57", red: "#DC2626", amb: "#D4763A", cyn: "#3D8B8B", pur: "#6B5B95",
  accent: "#FAFAFA", border: "rgba(217,219,221,0.12)", inputBg: "rgba(255,255,255,0.70)",
};

// ── Helper: format timestamp ──
function fmtTime(ts) {
  const d = new Date(ts);
  const mo = String(d.getMonth()+1).padStart(2,"0");
  const dy = String(d.getDate()).padStart(2,"0");
  const yr = String(d.getFullYear()).slice(-2);
  let h = d.getHours(); const ap = h >= 12 ? "PM" : "AM"; h = h % 12 || 12;
  const mn = String(d.getMinutes()).padStart(2,"0");
  return mo + "/" + dy + "/" + yr + " " + h + ":" + mn + " " + ap;
}

// ── Helper: parse @mentions ──
function renderText(text, contacts, deals, onClickContact, onClickDeal) {
  if (!text) return null;
  const parts = text.split(/(@\[.*?\]\(.*?\))/g);
  return parts.map((part, i) => {
    const mentionMatch = part.match(/@\[(.*?)\]\((.*?):(.*?)\)/);
    if (mentionMatch) {
      const [, name, type, id] = mentionMatch;
      const color = type === "contact" ? C.cyn : type === "deal" ? C.pur : C.accent;
      const onClick = type === "contact" ? () => onClickContact?.(id) : type === "deal" ? () => onClickDeal?.(id) : null;
      return <span key={i} onClick={onClick} style={{ color, fontWeight: 400, cursor: onClick ? "pointer" : "default", background: color + "15", padding: "1px 4px", borderRadius: 4 }}>@{name}</span>;
    }
    return <span key={i}>{part}</span>;
  });
}

export default function TeamChatPage({ onSelectContact, onSelectDeal, isMobile }) {
  const store = useStore();
  const auth = useAuth();
  const toast = useToast();
  const { contacts, deals, teamMembers, currentUserId, currentUser } = store;

  // Mobile: toggle between channel list and chat view
  const [mobileShowChat, setMobileShowChat] = useState(false);

  // State
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [loading, setLoading] = useState(true);
  const loadStartRef = useRef(Date.now());
  const finishLoading = useCallback(() => {
    const elapsed = Date.now() - loadStartRef.current;
    const remaining = Math.max(0, 500 - elapsed);
    setTimeout(() => setLoading(false), remaining);
  }, []);
  const [sending, setSending] = useState(false);
  const [showNewChannel, setShowNewChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDesc, setNewChannelDesc] = useState("");
  const [showDmPicker, setShowDmPicker] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionResults, setMentionResults] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState("channels");
  const [sendError, setSendError] = useState(null);
  const [hoveredMsg, setHoveredMsg] = useState(null);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [gifSearch, setGifSearch] = useState("");
  const [gifResults, setGifResults] = useState([]);
  const [gifLoading, setGifLoading] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [channelMembers, setChannelMembers] = useState([]);
  const [threadParent, setThreadParent] = useState(null); // message being threaded
  const [threadMessages, setThreadMessages] = useState([]);
  const [threadText, setThreadText] = useState("");
  const [dmPickerSelected, setDmPickerSelected] = useState([]); // multi-select for group DMs
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const recordingTimeRef = useRef(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const sttRef = useRef(null);
  const toggleSpeechToText = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Speech recognition not supported in this browser."); return; }
    if (isTranscribing && sttRef.current) { sttRef.current.stop(); setIsTranscribing(false); return; }
    const recognition = new SR();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";
    let finalText = msgText;
    recognition.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) { finalText += (finalText ? " " : "") + e.results[i][0].transcript; }
        else { interim += e.results[i][0].transcript; }
      }
      setMsgText(finalText + (interim ? " " + interim : ""));
    };
    recognition.onend = () => { setIsTranscribing(false); sttRef.current = null; };
    recognition.onerror = () => { setIsTranscribing(false); sttRef.current = null; };
    sttRef.current = recognition;
    recognition.start();
    setIsTranscribing(true);
  };

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const orgId = window.__coreflow_org_id || auth.currentUser?._orgId;

  // ── Load channels ──
  useEffect(() => {
    if (!orgId || !currentUserId) return;
    loadChannels();
  }, [orgId, currentUserId]);

  const loadChannels = async () => {
    try {
      const { data, error } = await supabase
        .from("chat_channels")
        .select("*")
        .eq("org_id", orgId)
        .order("is_default", { ascending: false })
        .order("created_at", { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        // Filter DMs: only show DMs where the current user is a participant
        const filtered = data.filter(ch => {
          if (ch.type !== "dm") return true; // show all regular channels
          if (!currentUserId) return false; // don't show any DMs until we know who we are
          return Array.isArray(ch.dm_user_ids) && ch.dm_user_ids.includes(currentUserId);
        });
        setChannels(filtered);
        if (!activeChannel) setActiveChannel(filtered[0]);
        finishLoading();
      } else {
        const { data: recheck } = await supabase.from("chat_channels").select("*").eq("org_id", orgId).limit(1);
        if (recheck && recheck.length > 0) {
          setChannels(recheck);
          setActiveChannel(recheck[0]);
        } else {
          const defaults = [
            { org_id: orgId, name: "general", description: "General team discussion", is_default: true, type: "channel" },
            { org_id: orgId, name: "deals", description: "Deal updates and pipeline talk", is_default: true, type: "channel" },
            { org_id: orgId, name: "wins", description: "Celebrate closed deals", is_default: true, type: "channel" },
          ];
          const { data: seeded } = await supabase.from("chat_channels").insert(defaults).select();
          if (seeded) {
            setChannels(seeded);
            setActiveChannel(seeded[0]);
          }
        }
        finishLoading();
      }
    } catch (e) {
      console.warn("[Chat] Channel load failed:", e.message);
      finishLoading();
    }
  };

  // ── Load messages for active channel ──
  useEffect(() => {
    if (!activeChannel?.id) return;
    setLoading(true);
    loadStartRef.current = Date.now();
    loadMessages(activeChannel.id);

    // Clear unread badge for this channel
    setUnreadCounts(prev => {
      if (!prev[activeChannel.id]) return prev;
      const next = { ...prev };
      delete next[activeChannel.id];
      return next;
    });

    // Mark as read in DB
    if (currentUserId) {
      supabase.from("chat_read_status").upsert({
        user_id: currentUserId, channel_id: activeChannel.id, last_read_at: new Date().toISOString(),
      }, { onConflict: "user_id,channel_id" }).then(({ error }) => {
        if (error) {
          console.error("[TeamChatPage] Failed to update read status:", error);
          toast?.error?.("Failed to update read status: " + error.message);
        }
      });
    }
  }, [activeChannel?.id]);

  // ── Auto-select channel from notification click ──
  useEffect(() => {
    const target = window.__coreflow_notification_target;
    if (target?.category === 'mention' && target.meta?.channelId && channels.length > 0) {
      const match = channels.find(ch => ch.id === target.meta.channelId);
      if (match) {
        setActiveChannel(match);
        if (match.type === 'dm') setTab('dms');
        else setTab('channels');
      }
      window.__coreflow_notification_target = null;
    }
  }, [channels]);

  const loadMessages = async (channelId) => {
    if (!orgId || !channelId) return;
    const { data, error } = await supabase
      .from("chat_messages")
      .select("*")
      .eq("channel_id", channelId)
      .eq("org_id", orgId)
      .order("created_at", { ascending: true })
      .limit(50);
    if (error) { console.warn("[Chat] Load messages failed:", error.message); finishLoading(); return; }
    if (data) setMessages(data);
    finishLoading();
  };

  // ── Real-time subscription for active channel messages ──
  useEffect(() => {
    if (!activeChannel?.id) return;

    const sub = supabase
      .channel("chat-" + activeChannel.id)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "chat_messages",
        filter: "channel_id=eq." + activeChannel.id,
      }, (payload) => {
        const newMsg = payload.new;
        // If it's a thread reply, add to thread messages (not main feed)
        if (newMsg.metadata?.thread_parent_id) {
          setThreadMessages(prev => {
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          // Update reply count on parent in main feed
          setMessages(prev => prev.map(m => {
            if (m.id === newMsg.metadata.thread_parent_id) {
              return { ...m, metadata: { ...m.metadata, reply_count: (m.metadata?.reply_count || 0) + 1 } };
            }
            return m;
          }));
          return;
        }
        setMessages(prev => {
          // Dedup check
          if (prev.some(m => m.id === newMsg.id)) return prev;
          return [...prev, newMsg];
        });
        
        // Update read status
        if (currentUserId) {
          supabase.from("chat_read_status").upsert({
            user_id: currentUserId, channel_id: activeChannel.id, last_read_at: new Date().toISOString(),
          }, { onConflict: "user_id,channel_id" }).then(() => {});
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(sub); };
  }, [activeChannel?.id]);

  // ── Real-time subscription for unread counts on OTHER channels ──
  useEffect(() => {
    if (!orgId) return;

    const globalSub = supabase
      .channel("chat-global-" + orgId)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "chat_messages",
        filter: "org_id=eq." + orgId,
      }, (payload) => {
        const msgChannelId = payload.new.channel_id;
        // If it's not the active channel, bump unread count
        if (msgChannelId !== activeChannel?.id) {
          setUnreadCounts(prev => ({
            ...prev,
            [msgChannelId]: (prev[msgChannelId] || 0) + 1,
          }));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(globalSub); };
  }, [orgId, activeChannel?.id]);

  // ── Real-time subscription for new DM channels (so they appear without refresh) ──
  useEffect(() => {
    if (!orgId || !currentUserId) return;

    const channelSub = supabase
      .channel("chat-channels-" + orgId)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "chat_channels",
        filter: "org_id=eq." + orgId,
      }, (payload) => {
        const newChannel = payload.new;
        // Only add DMs where the current user is a participant
        if (newChannel.type === "dm") {
          if (!Array.isArray(newChannel.dm_user_ids) || !newChannel.dm_user_ids.includes(currentUserId)) return;
        }
        // Add to channel list if not already there
        setChannels(prev => {
          if (prev.some(ch => ch.id === newChannel.id)) return prev;
          return [...prev, newChannel];
        });
      })
      .subscribe();

    return () => { supabase.removeChannel(channelSub); };
  }, [orgId, currentUserId]);

  // ── Unread counts (background, non-blocking) ──
  const unreadLoadedRef = useRef(false);
  useEffect(() => {
    if (!currentUserId || channels.length === 0 || unreadLoadedRef.current) return;
    unreadLoadedRef.current = true;
    // Delay so it doesn't compete with message loading
    const timer = setTimeout(async () => {
      try {
        const { data: readData } = await supabase
          .from("chat_read_status")
          .select("channel_id, last_read_at")
          .eq("user_id", currentUserId);
        const readMap = {};
        (readData || []).forEach(r => { readMap[r.channel_id] = r.last_read_at; });

        const counts = {};
        await Promise.all(channels.map(async (ch) => {
          const lastRead = readMap[ch.id] || "1970-01-01";
          const { count } = await supabase
            .from("chat_messages")
            .select("id", { count: "exact", head: true })
            .eq("channel_id", ch.id)
            .eq("org_id", orgId)
            .gt("created_at", lastRead);
          if (count > 0) counts[ch.id] = count;
        }));
        setUnreadCounts(counts);
      } catch (e) { console.warn("[Chat] Unread count failed:", e.message); }
    }, 1500);
    return () => clearTimeout(timer);
  }, [currentUserId, channels]);

  // Re-sign expired storage URLs — if URL has expired token, re-sign from path
  const signedUrlCache = useRef({});
  const getMediaUrl = useCallback(async (url, path) => {
    if (!path) return url; // GIFs and external URLs don't need re-signing
    const cacheKey = path;
    const cached = signedUrlCache.current[cacheKey];
    if (cached && cached.expiry > Date.now()) return cached.url;
    try {
      const { data } = await supabase.storage.from("chat-files").createSignedUrl(path, 60 * 60 * 24); // 24hr
      if (data?.signedUrl) {
        signedUrlCache.current[cacheKey] = { url: data.signedUrl, expiry: Date.now() + (23 * 60 * 60 * 1000) };
        return data.signedUrl;
      }
    } catch (e) { console.warn("[Chat] Re-sign failed:", e.message); }
    return url; // fallback to original URL
  }, []);

  // ── Reactions ──
  const REACTIONS = [];
  const toggleReaction = async (msgId, emoji) => {
    if (!currentUserId || !orgId) return;
    const msg = messages.find(m => m.id === msgId);
    if (!msg) return;
    // Snapshot for rollback
    const prevMessages = messages;
    // Optimistic update first
    const reactions = msg.metadata?.reactions || {};
    const users = reactions[emoji] || [];
    const hasReacted = users.includes(currentUserId);
    const newUsers = hasReacted ? users.filter(u => u !== currentUserId) : [...users, currentUserId];
    const newReactions = { ...reactions, [emoji]: newUsers };
    Object.keys(newReactions).forEach(k => { if (newReactions[k].length === 0) delete newReactions[k]; });
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, metadata: { ...m.metadata, reactions: newReactions } } : m));
    // Fetch fresh metadata from DB to avoid stale overwrites
    const { data: fresh, error: fetchErr } = await supabase.from("chat_messages").select("metadata").eq("id", msgId).eq("org_id", orgId).maybeSingle();
    if (fetchErr) {
      setMessages(prevMessages); // rollback
      console.error("[TeamChatPage] Failed to fetch reaction metadata:", fetchErr);
      toast?.error?.("Failed to update reaction: " + fetchErr.message);
      return;
    }
    if (!fresh) return;
    const freshReactions = fresh.metadata?.reactions || {};
    const freshUsers = freshReactions[emoji] || [];
    const freshHas = freshUsers.includes(currentUserId);
    const finalUsers = freshHas ? freshUsers.filter(u => u !== currentUserId) : [...freshUsers, currentUserId];
    const finalReactions = { ...freshReactions, [emoji]: finalUsers };
    Object.keys(finalReactions).forEach(k => { if (finalReactions[k].length === 0) delete finalReactions[k]; });
    const { error: updateErr } = await supabase.from("chat_messages").update({ metadata: { ...fresh.metadata, reactions: finalReactions } }).eq("id", msgId).eq("org_id", orgId);
    if (updateErr) {
      setMessages(prevMessages); // rollback
      console.error("[TeamChatPage] Failed to persist reaction:", updateErr);
      toast?.error?.("Failed to update reaction: " + updateErr.message);
    }
  };

  // ── Image upload ──
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeChannel || !orgId || !currentUserId) return;
    if (file.size > 5 * 1024 * 1024) { setSendError("File too large (max 5MB)"); setTimeout(() => setSendError(null), 3000); return; }

    const ext = file.name.split(".").pop();
    const path = `chat/${orgId}/${Date.now()}.${ext}`;

    try {
      const { error: uploadErr } = await supabase.storage.from("chat-files").upload(path, file);
      if (uploadErr) {
        // Bucket might not exist — try creating it
        if (uploadErr.message?.includes("not found") || uploadErr.statusCode === "404") {
          setSendError("Storage bucket 'chat-files' not found. Create it in Supabase Storage.");
          setTimeout(() => setSendError(null), 5000);
          return;
        }
        throw uploadErr;
      }
      const { data: urlData } = await supabase.storage.from("chat-files").createSignedUrl(path, 60 * 60 * 24 * 7); // 7 day expiry
      const url = urlData?.signedUrl;
      if (!url) throw new Error("No URL returned");

      // Send as message with attachment
      await supabase.from("chat_messages").insert({
        org_id: orgId, channel_id: activeChannel.id,
        user_id: currentUserId, sender_type: "user", sender_name: currentUser,
        text: msgText.trim() || "", metadata: { attachments: [{ url, path, type: file.type, name: file.name }] },
      });
      setMsgText("");
    } catch (err) {
      console.warn("[Chat] Upload failed:", err.message);
      setSendError("Upload failed: " + err.message);
      setTimeout(() => setSendError(null), 4000);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── GIF search via GIPHY ──
  const gifTimerRef = useRef(null);
  const searchGifs = async (query) => {
    if (!query.trim()) { setGifResults([]); setGifLoading(false); return; }
    setGifLoading(true);
    // Debounce — don't fire on every keystroke
    if (gifTimerRef.current) clearTimeout(gifTimerRef.current);
    gifTimerRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/giphy/search?q=${encodeURIComponent(query)}&limit=12&rating=pg`);
        if (!r.ok) throw new Error("GIPHY " + r.status);
        const data = await r.json();
        setGifResults((data.data || []).map(g => ({
          id: g.id,
          preview: g.images?.fixed_width?.url || g.images?.downsized?.url,
          full: g.images?.original?.url || g.images?.fixed_width?.url,
          title: g.title,
        })));
      } catch (e) {
        console.warn("[Chat] GIF search failed:", e.message);
        // Fallback: try Tenor v2 with web key
        try {
          const r2 = await fetch(`https://g.tenor.com/v1/search?q=${encodeURIComponent(query)}&key=LIVDSRZULELA&limit=12`);
          if (!r2.ok) throw new Error("Tenor " + r2.status);
          const data2 = await r2.json();
          setGifResults((data2.results || []).map(g => ({
            id: g.id,
            preview: g.media?.[0]?.tinygif?.url,
            full: g.media?.[0]?.gif?.url || g.media?.[0]?.tinygif?.url,
            title: g.title,
          })));
        } catch (e2) {
          console.warn("[Chat] Tenor fallback failed:", e2.message);
          setGifResults([]);
        }
      } finally {
        setGifLoading(false);
      }
    }, 400);
  };

  const sendGif = async (gif) => {
    if (!activeChannel || !orgId || !currentUserId) return;
    await supabase.from("chat_messages").insert({
      org_id: orgId, channel_id: activeChannel.id,
      user_id: currentUserId, sender_type: "user", sender_name: currentUser,
      text: "", metadata: { gif: { url: gif.full, preview: gif.preview, title: gif.title } },
    });
    setShowGifPicker(false);
    setGifSearch("");
    setGifResults([]);
  };

  // ── Voice recording ──
  const startRecording = async () => {
    if (!activeChannel || !orgId || !currentUserId) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) return; // Too short, ignore

        // Upload to Supabase Storage
        const path = `chat/${orgId}/voice-${Date.now()}.webm`;
        try {
          const { error: uploadErr } = await supabase.storage.from("chat-files").upload(path, blob);
          if (uploadErr) {
            setSendError("Voice upload failed. Create 'chat-files' bucket in Supabase Storage.");
            setTimeout(() => setSendError(null), 4000);
            return;
          }
          const { data: urlData } = await supabase.storage.from("chat-files").createSignedUrl(path, 60 * 60 * 24 * 7); // 7 day expiry
          const url = urlData?.signedUrl;
          if (!url) return;

          await supabase.from("chat_messages").insert({
            org_id: orgId, channel_id: activeChannel.id,
            user_id: currentUserId, sender_type: "user", sender_name: currentUser,
            text: "", metadata: { voice: { url, path, duration: recordingTimeRef.current } },
          });
        } catch (err) {
          console.warn("[Chat] Voice send failed:", err.message);
          setSendError("Voice message failed to send");
          setTimeout(() => setSendError(null), 3000);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      recordingTimeRef.current = 0;
      recordingTimerRef.current = setInterval(() => setRecordingTime(t => { recordingTimeRef.current = t + 1; return t + 1; }), 1000);
    } catch (err) {
      console.warn("[Chat] Mic access denied:", err.message);
      setSendError("Microphone access denied");
      setTimeout(() => setSendError(null), 3000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stream?.getTracks().forEach(t => t.stop());
      mediaRecorderRef.current = null;
      audioChunksRef.current = [];
      setIsRecording(false);
      setRecordingTime(0);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    }
  };

  // ── Channel members ──
  const loadChannelMembers = async () => {
    // For now all org members are in all channels
    // In future: store per-channel membership
    setChannelMembers(teamMembers.map(m => ({ ...m, inChannel: true })));
    setShowMembers(true);
  };

  const toggleMember = (member) => {
    setChannelMembers(prev => prev.map(m =>
      m._dbId === member._dbId ? { ...m, inChannel: !m.inChannel } : m
    ));
    // In future: persist to a channel_members table
  };

  // ── Send message ──
  const sendMessage = async () => {
    if (!msgText.trim() || !activeChannel?.id || sending || !orgId || !currentUserId) return;
    setSending(true);
    const text = msgText.trim();
    setMsgText("");

    try {
      setSendError(null);
      const { error } = await supabase.from("chat_messages").insert({
        org_id: orgId,
        channel_id: activeChannel.id,
        user_id: currentUserId,
        sender_type: "user",
        sender_name: currentUser,
        text,
        metadata: {},
      });
      if (error) throw error;
    } catch (e) {
      console.warn("[Chat] Send failed:", e.message);
      setMsgText(text); // restore on failure
      setSendError("Message failed to send. Try again.");
      setTimeout(() => setSendError(null), 4000);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  // ── @mention handling ──

  // ── Thread functions ──
  const openThread = async (msg) => {
    setThreadParent(msg);
    setThreadMessages([]);
    setThreadText("");
    // Load replies
    const { data } = await supabase.from("chat_messages")
      .select("*").eq("org_id", orgId)
      .eq("metadata->>thread_parent_id", msg.id)
      .order("created_at", { ascending: true });
    if (data) setThreadMessages(data);
  };

  const sendThreadReply = async () => {
    if (!threadText.trim() || !threadParent || !currentUserId) return;
    const text = threadText.trim();
    setThreadText("");
    const { data, error } = await supabase.from("chat_messages").insert({
      org_id: orgId, channel_id: activeChannel.id,
      user_id: currentUserId, sender_type: "user", sender_name: currentUser,
      text, metadata: { thread_parent_id: threadParent.id },
    }).select().single();
    if (error) { setSendError("Reply failed"); setTimeout(() => setSendError(null), 3000); return; }
    if (data) setThreadMessages(prev => [...prev, data]);
    // Update reply count on parent message metadata
    const currentCount = threadParent.metadata?.reply_count || 0;
    await supabase.from("chat_messages").update({
      metadata: { ...threadParent.metadata, reply_count: currentCount + 1 },
    }).eq("id", threadParent.id);
    setThreadParent(prev => ({ ...prev, metadata: { ...prev.metadata, reply_count: currentCount + 1 } }));
    // Update in main message list too
    setMessages(prev => prev.map(m => m.id === threadParent.id ? { ...m, metadata: { ...m.metadata, reply_count: currentCount + 1 } } : m));
  };

  // ── Group DM creation ──
  const createGroupDm = async () => {
    if (dmPickerSelected.length === 0 || !orgId || !currentUserId) return;
    const memberIds = [currentUserId, ...dmPickerSelected.map(m => m._dbId)];
    
    // If single person selected, use regular DM flow
    if (dmPickerSelected.length === 1) {
      await createDm(dmPickerSelected[0]);
      setDmPickerSelected([]);
      return;
    }

    // Check if group DM already exists with exact same members
    const existing = channels.find(ch => {
      if (ch.type !== "dm" || !Array.isArray(ch.dm_user_ids)) return false;
      if (ch.dm_user_ids.length !== memberIds.length) return false;
      return memberIds.every(id => ch.dm_user_ids.includes(id));
    });
    if (existing) { setActiveChannel(existing); setShowDmPicker(false); setDmPickerSelected([]); return; }

    const names = dmPickerSelected.map(m => m.name?.split(" ")[0]).join(", ");
    const { data, error } = await supabase.from("chat_channels").insert({
      org_id: orgId, name: `group-${Date.now()}`, type: "dm",
      dm_user_ids: memberIds, created_by: currentUserId,
      description: names,
    }).select().single();
    if (error) { console.warn("[Chat] Group DM failed:", error.message); return; }
    if (data) {
      setChannels(prev => [...prev, data]);
      setActiveChannel(data);
    }
    setShowDmPicker(false);
    setDmPickerSelected([]);
  };

  // ── @mention handling ──
  const handleInputChange = (e) => {
    const val = e.target.value;
    setMsgText(val);

    // Check for @mention trigger
    const lastAt = val.lastIndexOf("@");
    if (lastAt >= 0 && lastAt === val.length - 1 || (lastAt >= 0 && !val.slice(lastAt).includes(" "))) {
      const query = val.slice(lastAt + 1).toLowerCase();
      if (query.length >= 1) {
        const results = [
          ...contacts.filter(c => (c.fn + " " + c.ln).toLowerCase().includes(query)).slice(0, 4).map(c => ({ type: "contact", id: c.id, name: c.fn + " " + c.ln, sub: c.co })),
          ...deals.filter(d => d.name.toLowerCase().includes(query)).slice(0, 3).map(d => ({ type: "deal", id: d.id, name: d.name, sub: "$" + (d.val || 0).toLocaleString() })),
          ...teamMembers.filter(m => m.name.toLowerCase().includes(query)).slice(0, 3).map(m => ({ type: "user", id: m._dbId, name: m.name, sub: m.role })),
        ];
        setMentionResults(results);
        setMentionQuery(query);
      } else {
        setMentionResults([]);
        setMentionQuery(null);
      }
    } else {
      setMentionResults([]);
      setMentionQuery(null);
    }
  };

  const insertMention = (item) => {
    const lastAt = msgText.lastIndexOf("@");
    const before = msgText.slice(0, lastAt);
    const mention = `@[${item.name}](${item.type}:${item.id})`;
    setMsgText(before + mention + " ");
    setMentionResults([]);
    setMentionQuery(null);
    inputRef.current?.focus();
  };

  // ── Create channel ──
  const createChannel = async () => {
    if (!newChannelName.trim() || !orgId || !currentUserId) return;
    const name = newChannelName.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
    if (!name) return;
    // Check for duplicate channel name in this org
    const existing = channels.find(ch => ch.name === name && ch.type === "channel");
    if (existing) { setActiveChannel(existing); setShowNewChannel(false); setNewChannelName(""); setNewChannelDesc(""); return; }
    const { data, error } = await supabase.from("chat_channels").insert({
      org_id: orgId, name, description: newChannelDesc, type: "channel", created_by: currentUserId,
    }).select().single();
    if (error) { console.warn("[Chat] Create channel failed:", error.message); return; }
    if (data) {
      setChannels(prev => [...prev, data]);
      setActiveChannel(data);
    }
    setShowNewChannel(false);
    setNewChannelName("");
    setNewChannelDesc("");
  };

  // ── Create DM ──
  const createDm = async (member) => {
    if (!orgId || !currentUserId || !member?._dbId) return;
    // Check if DM already exists in local state
    const existing = channels.find(ch =>
      ch.type === "dm" && Array.isArray(ch.dm_user_ids) && ch.dm_user_ids.indexOf(currentUserId) !== -1 && ch.dm_user_ids.indexOf(member._dbId) !== -1
    );
    if (existing) { setActiveChannel(existing); setShowDmPicker(false); return; }

    // Also check the database (the other user may have created it from their side)
    try {
      const { data: dbChannels } = await supabase.from("chat_channels")
        .select("*").eq("org_id", orgId).eq("type", "dm")
        .contains("dm_user_ids", [currentUserId])
        .contains("dm_user_ids", [member._dbId]);
      if (dbChannels && dbChannels.length > 0) {
        // DM exists in DB but wasn't in our local state — add it and select it
        const found = dbChannels[0];
        setChannels(prev => prev.some(ch => ch.id === found.id) ? prev : [...prev, found]);
        setActiveChannel(found);
        setShowDmPicker(false);
        return;
      }
    } catch (e) { console.warn("[Chat] DM lookup failed:", e.message); }

    const { data, error } = await supabase.from("chat_channels").insert({
      org_id: orgId, name: `dm-${currentUserId}-${member._dbId}`, type: "dm",
      dm_user_ids: [currentUserId, member._dbId], created_by: currentUserId,
    }).select().single();
    if (error) { console.warn("[Chat] Create DM failed:", error.message); return; }
    if (data) {
      setChannels(prev => [...prev, data]);
      setActiveChannel(data);
    }
    setShowDmPicker(false);
  };

  // ── AI Analytics ──
  const requestAiAnalytics = async () => {
    if (!activeChannel || aiLoading || !orgId) return;
    setAiLoading(true);
    try {
      const prompt = `Analyze team performance in the #${activeChannel.name} channel. Here are the recent messages:\n\n${messages.slice(-30).map(m => `[${fmtTime(m.created_at)}] ${m.sender_name}: ${m.text}`).join("\n")}\n\nTeam members: ${teamMembers.map(m => m.name + " (" + m.role + ")").join(", ")}\n\nProvide a brief, insightful analysis of: 1) Activity level and engagement 2) Key topics discussed 3) Individual contributions 4) Actionable recommendations. Be specific and reference actual messages. Keep it under 200 words.`;

      const r = await apiFetch("/api/ai/specialty", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ command: prompt, action: "simple_chat", orgId }),
      });
      const data = await r.json();
      const reply = data.reply || data.response || "Unable to generate analysis.";

      // Post as AI message
      await supabase.from("chat_messages").insert({
        org_id: orgId, channel_id: activeChannel.id,
        sender_type: "ai", sender_name: "Core AI",
        text: "📊 **Channel Analysis**\n\n" + reply, metadata: { type: "ai_analytics" },
      });
    } catch (e) {
      console.warn("[Chat] AI analytics failed:", e.message);
    } finally {
      setAiLoading(false);
    }
  };

  // ── Get DM partner name ──
  const getDmName = (ch) => {
    if (ch.type !== "dm") return "#" + ch.name;
    if (!currentUserId || !Array.isArray(ch.dm_user_ids)) return "Direct Message";
    const otherId = ch.dm_user_ids.find(id => id && id !== currentUserId);
    if (!otherId) return "Direct Message";
    const member = teamMembers.find(m => m._dbId === otherId);
    return member?.name || "Direct Message";
  };

  // ── Sender avatar/color ──
  const getSenderColor = (msg) => {
    if (msg.sender_type === "ai") return C.pur;
    if (msg.sender_type === "system" || msg.sender_type === "workflow") return C.cyn;
    const member = teamMembers.find(m => m._dbId === msg.user_id);
    const colors = [C.blue, C.grn, C.amb, C.cyn, C.pur, C.red];
    const idx = teamMembers.indexOf(member);
    return colors[idx % colors.length] || C.blue;
  };

  // Separate channels and DMs
  const channelList = channels.filter(ch => ch.type === "channel");
  const dmList = channels.filter(ch => ch.type === "dm");


  if (loading) {
    return (
      <div style={{ width: "100%", height: "100%", background: "#2C2E33", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10 }}>
        <style>{`@keyframes cfLogoPulse { 0%,100% { opacity: 0.2; transform: scale(0.98); } @keyframes cfMsgIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } } 50% { opacity: 0.6; transform: scale(1.02); } }`}</style>
        <img src="/coreflow-logo-white.png" alt="CoreFlow" style={{ width: 160, opacity: 0.5, animation: "cfLogoPulse 2s ease-in-out infinite" }} />
      </div>
    );
  }

  const getMemberName = (ch) => {
    if (ch.type !== "dm") return ch.name;
    if (!currentUserId) return "Direct Message";
    if (Array.isArray(ch.dm_user_ids) && ch.dm_user_ids.length >= 2) {
      const otherIds = ch.dm_user_ids.filter(id => id && id !== currentUserId);
      // Group DM: show all other names
      if (otherIds.length > 1) {
        const names = otherIds.map(id => {
          const m = teamMembers.find(t => t._dbId === id);
          return m?.name?.split(" ")[0] || "?";
        });
        return names.join(", ");
      }
      // 1-on-1 DM
      const other = teamMembers.find(m => m._dbId === otherIds[0]);
      if (other) return other.name;
    }
    if (ch.name) {
      for (const m of teamMembers) {
        if (m._dbId && m._dbId !== currentUserId && ch.name.includes(m._dbId)) return m.name;
      }
    }
    const otherName = ch.name?.split(" & ").find(n => n !== (currentUser?.name || ""));
    return otherName || "Direct Message";
  };

  return (
    <div style={{ display: "flex", height: "100%", fontFamily: "inherit" }}>
      <style>{`@keyframes tcPulse{0%,100%{opacity:.3}50%{opacity:1}}`}</style>

      {/* ═══ LEFT PANEL — Channels & DMs ═══ */}
      {(!isMobile || !mobileShowChat) && <div style={{ width: isMobile ? "100%" : 240, flexShrink: 0, borderRight: isMobile ? "none" : "1px solid rgba(217,219,221,0.08)", display: "flex", flexDirection: "column", padding: "16px 0" }}>

        {/* Channels header */}
        <div style={{ padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", textTransform: "uppercase" }}>Channels</span>
          <button onClick={() => setShowNewChannel(true)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
            <Icon name="plus" size={14} color={T.tx3} />
          </button>
        </div>

        {/* Channel list */}
        {channelList.map(ch => {
          const active = activeChannel?.id === ch.id;
          const unread = unreadCounts[ch.id] || 0;
          return (
            <button key={ch.id} onClick={() => { setActiveChannel(ch); setTab("channels"); if (isMobile) setMobileShowChat(true); }}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", margin: "1px 8px",
                borderRadius: 8, border: "none", cursor: "pointer", width: "calc(100% - 16px)",
                background: active ? "rgba(64,89,173,0.12)" : "transparent",
                color: active ? "#FAFAFA" : T.tx2, fontSize: 13, fontWeight: active ? 700 : 400,
                textAlign: "left", fontFamily: "inherit",
              }}>
              <span style={{ color: active ? "#4059AD" : T.tx3, fontSize: 14 }}>#</span>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{ch.name}</span>
              {unread > 0 && <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: "#4059AD", color: "#FAFAFA", fontSize: 10, fontWeight: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{unread}</span>}
            </button>
          );
        })}

        {/* DMs header */}
        <div style={{ padding: "0 16px", display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 20, marginBottom: 8 }}>
          <span style={{ fontSize: 10, fontWeight: 400, color: T.tx3, letterSpacing: ".08em", textTransform: "uppercase" }}>Direct Messages</span>
          <button onClick={() => setShowDmPicker(true)} style={{ background: "none", border: "none", cursor: "pointer", padding: 2 }}>
            <Icon name="plus" size={14} color={T.tx3} />
          </button>
        </div>

        {/* DM list */}
        {dmList.map(ch => {
          const active = activeChannel?.id === ch.id;
          const unread = unreadCounts[ch.id] || 0;
          const name = getMemberName(ch);
          const otherId = (Array.isArray(ch.dm_user_ids) && currentUserId) ? ch.dm_user_ids.find(id => id && id !== currentUserId) : null;
          const otherMember = otherId ? teamMembers.find(m => m._dbId === otherId) : null;
          const initials = name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) || "?";
          return (
            <button key={ch.id} onClick={() => { setActiveChannel(ch); setTab("people"); if (isMobile) setMobileShowChat(true); }}
              style={{
                display: "flex", alignItems: "center", gap: 8, padding: "7px 16px", margin: "1px 8px",
                borderRadius: 8, border: "none", cursor: "pointer", width: "calc(100% - 16px)",
                background: active ? "rgba(64,89,173,0.12)" : "transparent",
                color: active ? "#FAFAFA" : T.tx2, fontSize: 13, fontWeight: active ? 700 : 400,
                textAlign: "left", fontFamily: "inherit",
              }}>
              {otherMember?.avatarUrl ? (
                <img src={otherMember.avatarUrl} alt="" style={{ width: 24, height: 24, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
              ) : (
                <div style={{ width: 24, height: 24, borderRadius: 6, background: "rgba(64,89,173,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 400, color: "#4059AD", flexShrink: 0 }}>{initials}</div>
              )}
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
              {unread > 0 && <span style={{ minWidth: 18, height: 18, borderRadius: 9, background: "#4059AD", color: "#FAFAFA", fontSize: 10, fontWeight: 400, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{unread}</span>}
            </button>
          );
        })}

        {/* New Channel Modal */}
        {showNewChannel && (
          <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={() => setShowNewChannel(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
            <div style={{ position: "relative", width: isMobile ? "92vw" : 400, background: "#2C2E33", borderRadius: 8, padding: isMobile ? 16 : 24, border: "1px solid rgba(217,219,221,0.12)" }}>
              <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA", marginBottom: 16 }}>Create Channel</div>
              <input value={newChannelName} onChange={e => setNewChannelName(e.target.value)} placeholder="channel-name" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 13, outline: "none", marginBottom: 10, fontFamily: "inherit", boxSizing: "border-box" }} />
              <input value={newChannelDesc} onChange={e => setNewChannelDesc(e.target.value)} placeholder="Description (optional)" style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 13, outline: "none", marginBottom: 16, fontFamily: "inherit", boxSizing: "border-box" }} />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setShowNewChannel(false)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx2, fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                <button onClick={async () => {
                  if (!newChannelName.trim()) return;
                  const slug = newChannelName.toLowerCase().replace(/[^a-z0-9-]/g, "-").replace(/-+/g, "-");
                  await supabase.from("chat_channels").insert({ org_id: orgId, name: slug, description: newChannelDesc, type: "channel", created_by: currentUserId });
                  setShowNewChannel(false); setNewChannelName(""); setNewChannelDesc("");
                  loadChannels();
                }} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Create</button>
              </div>
            </div>
          </div>
        )}

        {/* DM Picker Modal — Multi-select */}
        {showDmPicker && (
          <div style={{ position: "fixed", inset: 0, zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div onClick={() => { setShowDmPicker(false); setDmPickerSelected([]); }} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
            <div style={{ position: "relative", width: isMobile ? "92vw" : 360, background: "#2C2E33", borderRadius: 8, padding: isMobile ? 16 : 24, border: "1px solid rgba(217,219,221,0.12)", maxHeight: "60vh", display: "flex", flexDirection: "column" }}>
              <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA", marginBottom: 12 }}>New Message</div>
              {dmPickerSelected.length > 0 && (
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12, padding: "8px 0", borderBottom: "1px solid rgba(217,219,221,0.08)" }}>
                  {dmPickerSelected.map(m => (
                    <span key={m._dbId} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 8px", borderRadius: 6, background: "rgba(64,89,173,0.12)", color: "#FAFAFA", fontSize: 11 }}>
                      {m.name?.split(" ")[0]}
                      <button onClick={() => setDmPickerSelected(prev => prev.filter(p => p._dbId !== m._dbId))} style={{ background: "none", border: "none", cursor: "pointer", color: T.tx3, fontSize: 13, padding: 0, fontFamily: "inherit" }}>×</button>
                    </span>
                  ))}
                </div>
              )}
              <div style={{ overflowY: "auto", flex: 1 }}>
                {teamMembers.filter(m => m._dbId !== currentUserId).map(m => {
                  const selected = dmPickerSelected.some(s => s._dbId === m._dbId);
                  return (
                    <button key={m._dbId} onClick={() => {
                      if (selected) setDmPickerSelected(prev => prev.filter(p => p._dbId !== m._dbId));
                      else setDmPickerSelected(prev => [...prev, m]);
                    }} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, border: "none", background: selected ? "rgba(64,89,173,0.12)" : "transparent", cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit" }}
                      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = "rgba(64,89,173,0.08)"; }}
                      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "transparent"; }}>
                      {m.avatarUrl ? (
                        <img src={m.avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover" }} />
                      ) : (
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(64,89,173,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 400, color: "#4059AD" }}>{m.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}</div>
                      )}
                      <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>{m.name}</div><div style={{ fontSize: 10, color: T.tx3 }}>{m.role}</div></div>
                      {selected && <span style={{ color: "#4059AD", fontSize: 16 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(217,219,221,0.08)" }}>
                <button onClick={() => { setShowDmPicker(false); setDmPickerSelected([]); }} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx2, fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
                <button onClick={createGroupDm} disabled={dmPickerSelected.length === 0} style={{ padding: "8px 16px", borderRadius: 8, border: "none", background: dmPickerSelected.length > 0 ? "#4059AD" : "rgba(64,89,173,0.3)", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: dmPickerSelected.length > 0 ? "pointer" : "default", fontFamily: "inherit" }}>
                  {dmPickerSelected.length <= 1 ? "Message" : `Group Message (${dmPickerSelected.length})`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>}

      {/* ═══ RIGHT PANEL — Messages ═══ */}
      {(!isMobile || mobileShowChat) && <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* Channel header */}
        {activeChannel && (
          <div style={{ padding: isMobile ? "10px 14px" : "14px 24px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isMobile && <button onClick={() => setMobileShowChat(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", alignItems: "center" }}>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M11 4L6 9L11 14" stroke="#FAFAFA" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>}
              <div>
              <div style={{ fontSize: isMobile ? 14 : 15, fontWeight: 400, color: "#FAFAFA" }}>
                {activeChannel.type === "channel" ? "# " + activeChannel.name : getMemberName(activeChannel)}
              </div>
              {activeChannel.description && <div style={{ fontSize: 11, color: T.tx3, marginTop: 2 }}>{activeChannel.description}</div>}
            </div></div>
            <div style={{ display: "flex", gap: 6 }}>
              {activeChannel.type !== "dm" && (
              <button onClick={() => setShowMembers(!showMembers)} style={{ padding: "5px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "transparent", color: T.tx2, fontSize: 11, fontWeight: 400, cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                <Icon name="users" size={13} color={T.tx3} /> Members
              </button>
              )}
            </div>
          </div>
        )}

        {/* Messages feed */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 10px" : "16px 24px", display: "flex", flexDirection: "column-reverse" }}>
          {!activeChannel ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.tx3, fontSize: 13 }}>Select a channel to start messaging</div>
          ) : messages.length === 0 ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: T.tx3, fontSize: 13 }}>No messages yet. Start the conversation!</div>
          ) : (
            <div>
              {messages.filter(m => !m.metadata?.thread_parent_id).map((msg, idx) => {
                const isOwn = msg.user_id === currentUserId;
                const isSystem = msg.sender_type === "system" || msg.sender_type === "workflow" || msg.sender_type === "ai";
                const prevMsg = messages[idx - 1];
                const sameSender = prevMsg && prevMsg.user_id === msg.user_id && prevMsg.sender_type === msg.sender_type && (new Date(msg.created_at) - new Date(prevMsg.created_at)) < 120000;
                const member = teamMembers.find(m => m._dbId === msg.user_id);
                const senderName = isSystem ? (msg.sender_name || "CoreFlow") : (member?.name || msg.sender_name || "Unknown");
                const initials = senderName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
                const avatarUrl = member?.avatarUrl || member?.avatar_url;
                const reactions = msg.metadata?.reactions || {};

                return (
                  <div key={msg.id} style={{ marginBottom: sameSender ? 4 : 12 }}
                    onMouseEnter={() => setHoveredMsg(msg.id)} onMouseLeave={() => setHoveredMsg(null)}>

                    {/* Timestamp + name above bubble group */}
                    {!sameSender && (
                      <div style={{ display: "flex", justifyContent: isOwn ? "flex-end" : "flex-start", marginBottom: 4, paddingLeft: isOwn ? 0 : 42, paddingRight: isOwn ? 42 : 0 }}>
                        <span style={{ fontSize: 10, color: T.tx3 }}>
                          {!isOwn && <span style={{ fontWeight: 400, color: isSystem ? "#4059AD" : T.tx2, marginRight: 6 }}>{senderName}</span>}
                          {fmtTime(msg.created_at)}
                          {isOwn && <span style={{ fontWeight: 400, color: T.tx2, marginLeft: 6 }}>{senderName}</span>}
                        </span>
                      </div>
                    )}

                    {/* Message row */}
                    <div style={{ display: "flex", justifyContent: isOwn ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8, position: "relative" }}>

                      {/* Avatar left (others) */}
                      {!isOwn && !sameSender && (
                        avatarUrl ? (
                          <img src={avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: isSystem ? "rgba(64,89,173,0.15)" : "rgba(255,255,255,0.08)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 400, color: isSystem ? "#4059AD" : T.tx2, flexShrink: 0 }}>
                            {isSystem ? <Icon name="ai" size={14} color="#4059AD" /> : initials}
                          </div>
                        )
                      )}
                      {!isOwn && sameSender && <div style={{ width: 32, flexShrink: 0 }} />}

                      {/* Bubble */}
                      {(() => { const isMediaOnly = !msg.text && (msg.metadata?.gif || msg.metadata?.voice || msg.metadata?.attachments?.some(a => a.type?.startsWith("image/"))); return (
                      <div style={{
                        maxWidth: "65%", padding: isMediaOnly ? "2px 0" : "8px 12px",
                        borderRadius: 8,
                        borderBottomRightRadius: isOwn ? 4 : 14,
                        borderBottomLeftRadius: isOwn ? 14 : 4,
                        background: isMediaOnly ? "transparent" : (isOwn ? "#4059AD" : "#2C2E33"),
                        border: isMediaOnly ? "none" : (isOwn ? "none" : "1px solid rgba(217,219,221,0.10)"),
                        color: "#FAFAFA", fontSize: 13, lineHeight: 1.5,
                        whiteSpace: "pre-wrap", wordBreak: "break-word",
                      }}>
                        {/* Voice message */}
                        {msg.metadata?.voice && (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0" }}>
                            <audio src={msg.metadata.voice.url} controls style={{ height: 32, maxWidth: 220 }} />
                            {msg.metadata.voice.duration > 0 && <span style={{ fontSize: 10, color: "rgba(250,250,250,0.5)" }}>{msg.metadata.voice.duration}s</span>}
                          </div>
                        )}
                        {/* GIF */}
                        {msg.metadata?.gif && (
                          <img loading="lazy" src={msg.metadata.gif.preview || msg.metadata.gif.url} alt={msg.metadata.gif.title || "GIF"} style={{ maxWidth: 260, maxHeight: 200, borderRadius: 8, display: "block", marginBottom: msg.text ? 6 : 0 }} />
                        )}
                        {/* Attachments */}
                        {msg.metadata?.attachments?.map((att, ai) => (
                          att.type?.startsWith("image/") ? (
                            <img loading="lazy" key={ai} src={att.url} alt="" style={{ maxWidth: 260, maxHeight: 200, borderRadius: 8, marginBottom: msg.text ? 6 : 0, display: "block" }} />
                          ) : (
                            <a key={ai} href={att.url} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 8px", borderRadius: 6, background: "rgba(255,255,255,0.08)", color: "#FAFAFA", fontSize: 11, textDecoration: "none", marginBottom: msg.text ? 6 : 0 }}>
                              <Icon name="download" size={11} color="#FAFAFA" /> {att.name || "File"}
                            </a>
                          )
                        ))}
                        {msg.text && renderText(msg.text, contacts, deals, onSelectContact, onSelectDeal)}
                      </div>
                      ); })()}

                      {/* Avatar right (own) */}
                      {isOwn && !sameSender && (
                        avatarUrl ? (
                          <img src={avatarUrl} alt="" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "cover", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 32, height: 32, borderRadius: 8, background: "#4059AD", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 400, color: "#FAFAFA", flexShrink: 0 }}>{initials}</div>
                        )
                      )}
                      {isOwn && sameSender && <div style={{ width: 32, flexShrink: 0 }} />}

                      {/* Hover actions */}
                      {hoveredMsg === msg.id && (
                        <div style={{ position: "absolute", [isOwn ? "left" : "right"]: 0, top: -8, display: "flex", gap: 2, padding: "3px 6px", borderRadius: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", boxShadow: "0 2px 8px rgba(0,0,0,0.3)" }}>
                          {!msg.metadata?.thread_parent_id && (
                            <button onClick={() => openThread(msg)} style={{ width: 24, height: 24, borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", color: T.tx3 }}
                              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                              title="Reply in thread">
                              <Icon name="chat" size={12} color={T.tx3} />
                            </button>
                          )}
                          {["🔥", "👍", "❤️", "😂", "🎉"].map(emoji => (
                            <button key={emoji} onClick={() => toggleReaction(msg.id, emoji)} style={{ width: 24, height: 24, borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}
                              onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reactions below bubble */}
                    {Object.keys(reactions).length > 0 && (
                      <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap", justifyContent: isOwn ? "flex-end" : "flex-start", paddingLeft: isOwn ? 0 : 42, paddingRight: isOwn ? 42 : 0 }}>
                        {Object.entries(reactions).map(([emoji, users]) => (
                          <button key={emoji} onClick={() => toggleReaction(msg.id, emoji)} style={{
                            display: "flex", alignItems: "center", gap: 3, padding: "2px 8px", borderRadius: 8,
                            border: users.includes(currentUserId) ? "1px solid rgba(64,89,173,0.30)" : "1px solid rgba(217,219,221,0.10)",
                            background: users.includes(currentUserId) ? "rgba(64,89,173,0.10)" : "rgba(255,255,255,0.04)",
                            cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                          }}>
                            <span>{emoji}</span>
                            <span style={{ fontSize: 10, fontWeight: 400, color: users.includes(currentUserId) ? "#4059AD" : T.tx3 }}>{users.length}</span>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Thread reply count */}
                    {!msg.metadata?.thread_parent_id && (msg.metadata?.reply_count || 0) > 0 && (
                      <div style={{ display: "flex", justifyContent: isOwn ? "flex-end" : "flex-start", paddingLeft: isOwn ? 0 : 42, paddingRight: isOwn ? 42 : 0 }}>
                        <button onClick={() => openThread(msg)} style={{
                          display: "flex", alignItems: "center", gap: 4, marginTop: 4, padding: "3px 8px", borderRadius: 6,
                          border: "none", background: "transparent", cursor: "pointer", fontSize: 11, color: "#4059AD", fontFamily: "inherit",
                        }}
                          onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                          onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                          <Icon name="chat" size={11} color="#4059AD" /> {msg.metadata.reply_count} {msg.metadata.reply_count === 1 ? "reply" : "replies"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Members panel */}
        {showMembers && (
          <div style={{ position: "absolute", right: 24, top: 60, width: 240, background: "#2C2E33", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", padding: 12, boxShadow: "0 8px 32px rgba(0,0,0,0.3)", zIndex: 100 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 400, color: T.tx3 }}>Members</span>
              <button onClick={() => setShowMembers(false)} style={{ width: 20, height: 20, borderRadius: 6, border: "1px solid rgba(217,219,221,0.10)", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: T.tx3, fontFamily: "inherit" }}>×</button>
            </div>
            {channelMembers.length === 0 && teamMembers.map(m => (
              <div key={m._dbId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
                <div style={{ width: 24, height: 24, borderRadius: 8, background: "rgba(64,89,173,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 400, color: "#4059AD" }}>{m.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}</div>
                <span style={{ fontSize: 12, color: "#FAFAFA" }}>{m.name}</span>
              </div>
            ))}
          </div>
        )}

        {/* ═══ THREAD PANEL ═══ */}
        {threadParent && (
          <div style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: isMobile ? "100%" : 360, background: "#2C2E33", borderLeft: isMobile ? "none" : "1px solid rgba(217,219,221,0.12)", display: "flex", flexDirection: "column", zIndex: 200, boxShadow: isMobile ? "none" : "-4px 0 24px rgba(0,0,0,0.3)" }}>
            {/* Thread header */}
            <div style={{ padding: "14px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 13, fontWeight: 400, color: "#FAFAFA" }}>Thread</span>
              <button onClick={() => setThreadParent(null)} style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid rgba(217,219,221,0.10)", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: T.tx3, fontFamily: "inherit" }}>×</button>
            </div>
            {/* Parent message */}
            <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(217,219,221,0.06)", background: "rgba(255,255,255,0.02)" }}>
              <div style={{ fontSize: 10, color: T.tx3, marginBottom: 4 }}>
                {teamMembers.find(m => m._dbId === threadParent.user_id)?.name || threadParent.sender_name} · {fmtTime(threadParent.created_at)}
              </div>
              <div style={{ fontSize: 13, color: "#FAFAFA", lineHeight: 1.5 }}>{threadParent.text}</div>
            </div>
            {/* Thread replies */}
            <div style={{ flex: 1, overflowY: "auto", padding: "12px 16px" }}>
              {threadMessages.length === 0 && (
                <div style={{ textAlign: "center", color: T.tx3, fontSize: 12, padding: "24px 0" }}>No replies yet</div>
              )}
              {threadMessages.map(msg => {
                const member = teamMembers.find(m => m._dbId === msg.user_id);
                const name = member?.name || msg.sender_name || "Unknown";
                const isOwn = msg.user_id === currentUserId;
                return (
                  <div key={msg.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 10, color: T.tx3, marginBottom: 3 }}>
                      <span style={{ fontWeight: 400, color: T.tx2 }}>{name}</span> · {fmtTime(msg.created_at)}
                    </div>
                    <div style={{ padding: "6px 10px", borderRadius: 8, background: isOwn ? "#4059AD" : "rgba(255,255,255,0.06)", border: isOwn ? "none" : "1px solid rgba(217,219,221,0.08)", color: "#FAFAFA", fontSize: 13, lineHeight: 1.5 }}>
                      {msg.text}
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Thread input */}
            <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(217,219,221,0.08)" }}>
              <div style={{ display: "flex", gap: 8 }}>
                <input value={threadText} onChange={e => setThreadText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendThreadReply(); } }}
                  placeholder="Reply..."
                  style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit" }} />
                <button onClick={sendThreadReply} style={{ width: 32, height: 32, borderRadius: 8, border: "none", background: threadText.trim() ? "#4059AD" : "rgba(64,89,173,0.3)", cursor: threadText.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name="send" size={14} color="#FAFAFA" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ INPUT BAR ═══ */}
        {activeChannel && (
          <div style={{ padding: isMobile ? "10px 10px 14px" : "12px 24px 16px", borderTop: "1px solid rgba(217,219,221,0.08)" }}>

            {/* Mention suggestions */}
            {mentionResults.length > 0 && (
              <div style={{ marginBottom: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, overflow: "hidden" }}>
                {mentionResults.map((r, i) => (
                  <button key={r.id || i} onClick={() => {
                    const before = msgText.slice(0, msgText.lastIndexOf("@"));
                    setMsgText(before + `@[${r.name}](${r.type}:${r.id}) `);
                    setMentionResults([]); setMentionQuery(null);
                    inputRef.current?.focus();
                  }} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", border: "none", background: "transparent", cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit", color: "#FAFAFA", fontSize: 12 }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(64,89,173,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <span style={{ fontSize: 10, color: T.tx3 }}>{r.type}</span>
                    <span>{r.name}</span>
                  </button>
                ))}
              </div>
            )}

            {/* GIF picker */}
            {showGifPicker && (
              <div style={{ marginBottom: 8, background: "#2C2E33", border: "1px solid rgba(217,219,221,0.12)", borderRadius: 8, padding: 12, maxHeight: 280, overflow: "auto" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <input value={gifSearch} onChange={e => { setGifSearch(e.target.value); searchGifs(e.target.value); }} placeholder="Search GIFs..." style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: "none", background: "rgba(255,255,255,0.06)", color: "#FAFAFA", fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
                  <button onClick={() => setShowGifPicker(false)} style={{ width: 24, height: 24, borderRadius: 6, border: "1px solid rgba(217,219,221,0.10)", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: T.tx3, fontFamily: "inherit", flexShrink: 0 }}>×</button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6 }}>
                  {gifResults.map((g, i) => (
                    <img key={i} src={g.preview} alt="" onClick={() => sendGif(g)} style={{ width: "100%", borderRadius: 6, cursor: "pointer", aspectRatio: "1", objectFit: "cover" }} />
                  ))}
                  {gifLoading && <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 12, color: T.tx3, fontSize: 11 }}>Searching...</div>}
                  {!gifLoading && gifSearch && gifResults.length === 0 && <div style={{ gridColumn: "1/-1", textAlign: "center", padding: 12, color: T.tx3, fontSize: 11 }}>No GIFs found</div>}
                </div>
              </div>
            )}

            {/* Recording state */}
            {isRecording ? (
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", borderRadius: 8, background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.20)" }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: "#E53935", animation: "imPulse .6s ease infinite" }} />
                <span style={{ fontSize: 12, color: "#E53935", fontWeight: 400 }}>Recording {recordingTime}s</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => { stopRecording(); }} style={{ padding: "6px 16px", borderRadius: 8, border: "none", background: "#4059AD", color: "#FAFAFA", fontSize: 12, fontWeight: 400, cursor: "pointer", fontFamily: "inherit" }}>Send</button>
                <button onClick={cancelRecording} style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.12)", background: "transparent", color: T.tx3, fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
              </div>
            ) : (
              /* Normal input */
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="file" ref={fileInputRef} style={{ display: "none" }} onChange={handleImageUpload} />
                <button onClick={() => fileInputRef.current?.click()} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="upload" size={16} color={T.tx3} />
                </button>
                <button onClick={() => setShowGifPicker(!showGifPicker)} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 11, fontWeight: 400, color: T.tx3 }}>GIF</button>
                <div style={{ flex: 1, position: "relative" }}>
                  <textarea ref={inputRef} value={msgText}
                    onChange={e => {
                      setMsgText(e.target.value);
                      const txt = e.target.value;
                      const atIdx = txt.lastIndexOf("@");
                      if (atIdx >= 0 && atIdx === txt.length - 1) { setMentionQuery(""); }
                      else if (atIdx >= 0 && !txt.slice(atIdx).includes(" ")) {
                        const q = txt.slice(atIdx + 1).toLowerCase();
                        setMentionQuery(q);
                        const results = [
                          ...teamMembers.filter(m => m.name?.toLowerCase().includes(q)).map(m => ({ id: m._dbId, name: m.name, type: "member" })),
                          ...contacts.filter(c => ((c.fn || "") + " " + (c.ln || "")).toLowerCase().includes(q)).slice(0, 5).map(c => ({ id: c.id, name: (c.fn || "") + " " + (c.ln || ""), type: "contact" })),
                        ].slice(0, 8);
                        setMentionResults(results);
                      } else { setMentionResults([]); setMentionQuery(null); }
                    }}
                    onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } if (e.key === "Escape") { setMentionResults([]); setMentionQuery(null); setShowGifPicker(false); } }}
                    placeholder={`Message ${activeChannel.type === "channel" ? "#" + activeChannel.name : getMemberName(activeChannel)}...`}
                    rows={1}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(217,219,221,0.10)", background: "rgba(255,255,255,0.05)", color: "#FAFAFA", fontSize: 13, outline: "none", resize: "none", fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box" }} />
                </div>
                <button onClick={toggleSpeechToText} title={isTranscribing ? "Stop dictation" : "Dictate message"} style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: isTranscribing ? "rgba(64,89,173,0.25)" : "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isTranscribing ? "#4059AD" : T.tx3} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
                  {isTranscribing && <div style={{ position:"absolute", top:4, right:4, width:6, height:6, borderRadius:3, background:"#E53935", animation:"tcPulse .8s ease infinite" }} />}
                </button>
                <button onClick={startRecording} title="Send voice message" style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.tx3} strokeWidth="2"><circle cx="12" cy="12" r="6" fill={T.tx3} stroke="none"/><circle cx="12" cy="12" r="10"/></svg>
                </button>
                <button onClick={sendMessage} disabled={!msgText.trim() || sending}
                  style={{ width: 36, height: 36, borderRadius: 8, border: "none", background: msgText.trim() ? "#4059AD" : "transparent", cursor: msgText.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name="send" size={16} color={msgText.trim() ? "#FAFAFA" : T.tx3} />
                </button>
              </div>
            )}

            {sendError && <div style={{ fontSize: 10, color: "#E53935", marginTop: 6 }}>{sendError}</div>}
          </div>
        )}
      </div>}
    </div>
  );
}


export async function postToChatChannel(orgId, channelName, text, senderType = "workflow", senderName = "CoreFlow") {
  if (!orgId || !channelName || !text) { console.warn("[Chat] postToChatChannel: missing required params"); return; }
  try {
    const { data: ch } = await supabase
      .from("chat_channels")
      .select("id")
      .eq("org_id", orgId)
      .eq("name", channelName)
      .maybeSingle();
    if (!ch) { console.warn("[Chat] Channel not found:", channelName); return; }

    const { error } = await supabase.from("chat_messages").insert({
      org_id: orgId, channel_id: ch.id,
      sender_type: senderType, sender_name: senderName,
      text, metadata: { automated: true },
    });
    if (error) throw error;
    console.log("[Chat] Posted to #" + channelName);
  } catch (e) {
    console.warn("[Chat] Post failed:", e.message);
  }
}
